/**
 * Keyword Recommendations Modal
 * Displays AI-generated recommended search keywords with citations
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, Button, Badge } from '@tracekit/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRecommendedKeywords, streamRecommendedKeywords, getKeywordTaskStatus, RecommendedKeyword, KeywordStreamCallbacks, KeywordTaskStatus } from '../../api/opportunities';
import { useWorkspace } from '@tracekit/core';
import {
  X,
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  Sparkles,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Brain,
  Zap,
  StopCircle,
  MapPin,
  Building2,
  Briefcase,
} from 'lucide-react';
import { FilterSuggestions } from './FilterSuggestions';
import { getCitationDisplayLabel } from '../../utils/citationDisplayLabel';

interface KeywordRecommendationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectKeyword: (keyword: string) => void;
}

export function KeywordRecommendationsModal({
  isOpen,
  onClose,
  onSelectKeyword,
}: KeywordRecommendationsModalProps) {
  const { projectId } = useWorkspace();
  const [expandedKeywords, setExpandedKeywords] = useState<Set<string>>(new Set());
  const [refreshTimestamp, setRefreshTimestamp] = useState<number | null>(null);
  const queryClient = useQueryClient();
  
  // Streaming state
  const [useStreaming, setUseStreaming] = useState(true);
  const [streamingState, setStreamingState] = useState<{
    isStreaming: boolean;
    stage: string;
    message: string;
    elapsed: number;
    opportunities_count?: number;
    common_terms_count?: number;
    keywords: RecommendedKeyword[];
    location_keywords?: string[];
    department_names?: string[];
    location_count?: number;
    department_count?: number;
    location_keywords_with_count?: import('../../api/opportunities').KeywordFilterWithCount[];
    department_names_with_count?: import('../../api/opportunities').KeywordFilterWithCount[];
    total_opportunities_analyzed?: number;
    service_keywords?: import('../../api/opportunities').ServiceKeywordSubcategory[];
    service_keywords_count?: number;
    service_keyword_groups?: import('../../api/opportunities').ServiceKeywordGroup[];
    error?: Error;
  }>({
    isStreaming: false,
    stage: '',
    message: '',
    elapsed: 0,
    keywords: [],
    location_keywords: [],
    department_names: [],
  });
  const abortRef = useRef<(() => void) | null>(null);
  const startTimeRef = useRef<number>(0);

  // Fetch recommended keywords (non-streaming fallback)
  const {
    data: keywordsData,
    isLoading: isLoadingNonStreaming,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['recommended-keywords', projectId, refreshTimestamp],
    queryFn: () => getRecommendedKeywords(projectId!, refreshTimestamp !== null),
    enabled: isOpen && !!projectId && !useStreaming,
    staleTime: refreshTimestamp !== null ? 0 : 30 * 60 * 1000,
  });

  // Start streaming when modal opens
  useEffect(() => {
    if (isOpen && projectId && useStreaming && !streamingState.isStreaming && streamingState.keywords.length === 0) {
      startStreaming();
    }
    
    return () => {
      // Cleanup on unmount
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }
    };
  }, [isOpen, projectId, useStreaming]);

  // Update elapsed time during streaming
  useEffect(() => {
    if (!streamingState.isStreaming) return;
    
    const interval = setInterval(() => {
      if (startTimeRef.current > 0) {
        setStreamingState(prev => ({
          ...prev,
          elapsed: (Date.now() - startTimeRef.current) / 1000,
        }));
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [streamingState.isStreaming]);

  const startStreaming = async (forceBypassCache?: boolean) => {
    if (!projectId) return;
    const bypassCache = forceBypassCache ?? (refreshTimestamp !== null);

    startTimeRef.current = Date.now();
    setStreamingState({
      isStreaming: true,
      stage: 'starting',
      message: 'Initializing keyword generation...',
      elapsed: 0,
      keywords: [],
    });
    
    const callbacks: KeywordStreamCallbacks = {
      onStatus: (data) => {
        setStreamingState(prev => ({
          ...prev,
          stage: data.stage,
          message: data.message,
          opportunities_count: data.opportunities_count,
          common_terms_count: data.common_terms_count,
        }));
      },
      onKeywords: (keywords) => {
        setStreamingState(prev => ({
          ...prev,
          keywords: keywords,
        }));
      },
      onComplete: (data) => {
        setStreamingState(prev => ({
          ...prev,
          isStreaming: false,
          stage: 'complete',
          message: 'Keywords generated successfully!',
          keywords: data.keywords || [],
          opportunities_count: data.opportunities_analyzed || 0,
          common_terms_count: data.common_terms_found || 0,
          location_keywords: Array.isArray(data.location_keywords) ? data.location_keywords : [],
          department_names: Array.isArray(data.department_names) ? data.department_names : [],
          location_count: data.location_count || 0,
          department_count: data.department_count || 0,
          location_keywords_with_count: Array.isArray(data.location_keywords_with_count) ? data.location_keywords_with_count : undefined,
          department_names_with_count: Array.isArray(data.department_names_with_count) ? data.department_names_with_count : undefined,
          total_opportunities_analyzed: data.total_opportunities_analyzed ?? undefined,
          service_keywords: Array.isArray(data.service_keywords) ? data.service_keywords : [],
          service_keywords_count: data.service_keywords_count ?? 0,
          service_keyword_groups: Array.isArray(data.service_keyword_groups) ? data.service_keyword_groups : [],
        }));
        abortRef.current = null;
      },
      onError: (error) => {
        setStreamingState(prev => ({
          ...prev,
          isStreaming: false,
          stage: 'error',
          message: error.message,
          error: error,
        }));
        abortRef.current = null;
      },
    };
    
    const abort = await streamRecommendedKeywords(projectId, bypassCache, callbacks);
    abortRef.current = abort;
  };

  const handleForceRefresh = async () => {
    // Cancel current stream if running
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    
    const timestamp = Date.now();
    setRefreshTimestamp(timestamp);
    await queryClient.invalidateQueries({ queryKey: ['recommended-keywords', projectId] });
    
    setStreamingState({
      isStreaming: false,
      stage: '',
      message: '',
      elapsed: 0,
      keywords: [],
    });
    
    if (useStreaming) {
      await startStreaming(true);
    } else {
      await refetch();
    }
    
    // Reset timestamp after a delay
    setTimeout(() => {
      setRefreshTimestamp(null);
    }, 10000);
  };

  const handleCancelStream = () => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    setStreamingState(prev => ({
      ...prev,
      isStreaming: false,
      stage: 'cancelled',
      message: 'Generation cancelled',
    }));
  };

  // Determine which keywords to show: filter out score <= 5% (frontend redundancy with backend), then sort by confidence_pct desc
  const rawKeywords = useStreaming && streamingState.keywords.length > 0
    ? streamingState.keywords
    : keywordsData?.keywords || [];
  const MIN_CONFIDENCE_PCT = 5;
  const keywords = [...rawKeywords]
    .filter((kw) => (kw.confidence_pct ?? 0) > MIN_CONFIDENCE_PCT)
    .sort((a, b) => (b.confidence_pct ?? 0) - (a.confidence_pct ?? 0));

  const isLoading = useStreaming
    ? streamingState.isStreaming
    : isLoadingNonStreaming;

  // Location/department: prefer _with_count for % and sorting when present
  const locWithCount = useStreaming ? streamingState.location_keywords_with_count : keywordsData?.location_keywords_with_count;
  const deptWithCount = useStreaming ? streamingState.department_names_with_count : keywordsData?.department_names_with_count;
  const totalOpps = useStreaming ? streamingState.total_opportunities_analyzed : keywordsData?.total_opportunities_analyzed;
  const locationKeywords = (locWithCount?.length ? locWithCount.map((x: { name: string }) => x.name) : (useStreaming ? streamingState.location_keywords : keywordsData?.location_keywords)) || [];
  const departmentNames = (deptWithCount?.length ? deptWithCount.map((x: { name: string }) => x.name) : (useStreaming ? streamingState.department_names : keywordsData?.department_names)) || [];
  const locationCount = locationKeywords.length;
  const departmentCount = departmentNames.length;
  const serviceKeywordGroups = useStreaming
    ? (streamingState.service_keyword_groups || [])
    : (keywordsData?.service_keyword_groups || []);
  const serviceKeywordsCount = useStreaming
    ? (streamingState.service_keywords_count ?? 0)
    : (keywordsData?.service_keywords_count ?? 0);

  const toggleKeywordExpansion = (keyword: string) => {
    const newExpanded = new Set(expandedKeywords);
    if (newExpanded.has(keyword)) {
      newExpanded.delete(keyword);
    } else {
      newExpanded.add(keyword);
    }
    setExpandedKeywords(newExpanded);
  };

  const handleKeywordSelect = (keyword: string) => {
    onSelectKeyword(keyword);
    onClose();
  };

  const handleFilterSelect = (filterType: 'location' | 'department' | 'service', filterValue: string) => {
    // Service = semantic search (append title); location/department = field:value syntax
    const searchText = filterType === 'service'
      ? filterValue
      : `${filterType === 'location' ? 'city' : 'organization'}:"${filterValue}"`;
    onSelectKeyword(searchText);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-10 w-full max-w-4xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Sparkles className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-theme-text">
                  Recommended Search Keywords
                </h2>
                <p className="text-sm text-gray-600 dark:text-theme-text-secondary mt-1">
                  AI-generated keywords based on your company capabilities
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleForceRefresh}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {isLoading ? (
              <LoadingState
                streamingState={streamingState}
                onCancel={handleCancelStream}
              />
            ) : streamingState.error || isError ? (
              <Card className="p-8 text-center">
                <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-theme-text-secondary mb-2">
                  Failed to load keyword recommendations
                </p>
                <p className="text-sm text-gray-500 dark:text-theme-text-muted">
                  {streamingState.error?.message || (error instanceof Error ? error.message : 'An unexpected error occurred')}
                </p>
              </Card>
            ) : keywords.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-600 dark:text-theme-text-secondary">
                  No keyword recommendations available
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Main Keywords Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    Search Keywords ({keywords.length})
                  </h3>
                  <div className="space-y-3">
                    {keywords.map((keyword, index) => (
                      <KeywordCard
                        key={index}
                        keyword={keyword}
                        isExpanded={expandedKeywords.has(keyword.keyword)}
                        onToggleExpand={() => toggleKeywordExpansion(keyword.keyword)}
                        onSelect={() => handleKeywordSelect(keyword.keyword)}
                      />
                    ))}
                  </div>
                </div>

                {/* Location Filters Section (with % when total_opportunities_analyzed present) */}
                {locationKeywords.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-3 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Location Filters ({locationCount})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(locWithCount?.length ? locWithCount : locationKeywords.map((name: string) => ({ name, count: 0 }))).map((item: { name: string; count?: number }, index: number) => {
                        const name = typeof item === 'string' ? item : item.name;
                        const count = typeof item === 'object' && item != null && 'count' in item ? item.count : undefined;
                        const pct = totalOpps && count != null && count > 0 ? Math.round((count / totalOpps) * 100) : null;
                        return (
                          <button
                            key={`location-${index}`}
                            onClick={() => handleFilterSelect('location', name)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors"
                            title={pct != null && pct > 0 ? `${name} — ${pct}% of opportunities` : `Filter by location: ${name}`}
                          >
                            <MapPin className="h-4 w-4" />
                            <span>{name}</span>
                            {pct != null && pct > 0 && <span className="text-blue-600/80 dark:text-blue-300/80">({pct}%)</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Department Filters Section (with % when total_opportunities_analyzed present) */}
                {departmentNames.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-3 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Department Filters ({departmentCount})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(deptWithCount?.length ? deptWithCount : departmentNames.map((name: string) => ({ name, count: 0 }))).map((item: { name: string; count?: number }, index: number) => {
                        const name = typeof item === 'string' ? item : item.name;
                        const count = typeof item === 'object' && item != null && 'count' in item ? item.count : undefined;
                        const pct = totalOpps && count != null && count > 0 ? Math.round((count / totalOpps) * 100) : null;
                        return (
                          <button
                            key={`department-${index}`}
                            onClick={() => handleFilterSelect('department', name)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800 transition-colors"
                            title={pct != null && pct > 0 ? `${name} — ${pct}% of opportunities` : `Filter by department: ${name}`}
                          >
                            <Building2 className="h-4 w-4" />
                            <span>{name}</span>
                            {pct != null && pct > 0 && <span className="text-purple-600/80 dark:text-purple-300/80">({pct}%)</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Service / Department (policy-driven; semantic search) */}
                {serviceKeywordGroups.length > 0 && serviceKeywordsCount > 0 && (
                  <>
                    {serviceKeywordGroups.map((group: import('../../api/opportunities').ServiceKeywordGroup) => (
                      <div key={group.id}>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-3 flex items-center gap-2">
                          <Briefcase className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                          {group.label}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {(group.subcategories || []).map((sub: import('../../api/opportunities').ServiceKeywordSubcategory) => (
                            <button
                              key={`service-${group.id}-${sub.id}`}
                              onClick={() => handleFilterSelect('service', sub.title)}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/30 border border-teal-200 dark:border-teal-800 transition-colors"
                              title={`Search: ${sub.title}`}
                            >
                              <Briefcase className="h-4 w-4" />
                              <span>{sub.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
              {keywords.length > 0 && `${keywords.length} keywords available`}
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

interface KeywordCardProps {
  keyword: RecommendedKeyword;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
}

function KeywordCard({ keyword, isExpanded, onToggleExpand, onSelect }: KeywordCardProps) {
  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        {/* Keyword and Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="info" className="text-sm font-semibold px-3 py-1">
              {keyword.keyword}
            </Badge>
            {keyword.citation_count > 0 && (
              <span className="text-xs text-gray-500 dark:text-theme-text-muted">
                {keyword.citation_count} source{keyword.citation_count !== 1 ? 's' : ''}
              </span>
            )}
            {keyword.confidence_pct != null && (
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400" title="Relevance confidence">
                {keyword.confidence_pct}%
              </span>
            )}
          </div>
          
          {keyword.explanation && (
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-3">
              {keyword.explanation}
            </p>
          )}

          {/* Citations (expandable) */}
          {keyword.citations && keyword.citations.length > 0 && (
            <div>
              <button
                onClick={onToggleExpand}
                className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span>
                  {isExpanded ? 'Hide' : 'Show'} sources ({keyword.citations.length})
                </span>
              </button>

              {isExpanded && (
                <div className="mt-3 space-y-2 pl-6">
                  {keyword.citations.map((citation, idx) => (
                    <CitationCard key={idx} citation={citation} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button
          size="sm"
          onClick={onSelect}
          className="flex items-center gap-2 flex-shrink-0"
        >
          <Search className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );
}

interface CitationCardProps {
  citation: RecommendedKeyword['citations'][0];
}

function CitationCard({ citation }: CitationCardProps) {
  const artifactName = getCitationDisplayLabel(citation);
  
  const page = citation.page;
  const content = citation.content || '';
  
  // Normalize confidence score to 0-100 range
  // Backend may return either 0-1 or 0-100, so we need to handle both
  let relevanceScore = citation.relevance_score ?? citation.confidence ?? 0;
  
  // If score is > 1, assume it's already a percentage (0-100)
  // If score is <= 1, assume it's a 0-1 score and convert to percentage
  if (relevanceScore > 0) {
    if (relevanceScore <= 1.0) {
      // It's a 0-1 score, convert to percentage
      relevanceScore = relevanceScore * 100;
    }
    // Cap at 100% to avoid showing values > 100%
    relevanceScore = Math.min(100, Math.max(0, relevanceScore));
  }

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 dark:text-theme-text truncate">
            {artifactName}
          </span>
          {page && (
            <span className="text-xs text-gray-500 dark:text-theme-text-muted flex-shrink-0">
              Page {page}
            </span>
          )}
        </div>
        {relevanceScore > 0 && (
          <Badge variant="info" className="text-xs flex-shrink-0">
            {relevanceScore.toFixed(0)}%
          </Badge>
        )}
      </div>
      
      {content && (
        <p className="text-xs text-gray-600 dark:text-theme-text-secondary line-clamp-2 mt-1">
          {content}
        </p>
      )}
      
      {citation.artifact_id && (
        <a
          href={`/artifacts/${citation.artifact_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-2"
        >
          <ExternalLink className="h-3 w-3" />
          View document
        </a>
      )}
    </div>
  );
}

interface LoadingStateProps {
  streamingState: {
    isStreaming: boolean;
    stage: string;
    message: string;
    elapsed: number;
    opportunities_count?: number;
    common_terms_count?: number;
    keywords?: RecommendedKeyword[];
  };
  onCancel: () => void;
}

function LoadingState({ streamingState, onCancel }: LoadingStateProps) {
  const getStageDescription = (stage: string) => {
    switch (stage) {
      case 'analyzing_company':
        return 'Analyzing company capabilities and service lines to understand your expertise...';
      case 'analyzing_opportunities':
        return 'Analyzing existing opportunities to personalize keywords to real opportunity language...';
      case 'opportunities_analyzed':
        return `Found ${streamingState.opportunities_count || 0} opportunities and ${streamingState.common_terms_count || 0} common terms`;
      case 'querying_rag':
        return 'Querying AI to generate keywords based on company capabilities and opportunity patterns...';
      case 'extracting_keywords':
        return 'Extracting and validating keywords from AI response...';
      case 'scoring_keywords':
        return 'Scoring keywords against actual opportunity terms to prioritize the most relevant ones...';
      case 'starting':
        return 'Initializing keyword generation...';
      default:
        return 'Generating keyword recommendations...';
    }
  };
  
  const getStageIcon = (stage: string) => {
    if (stage === 'analyzing_company' || stage === 'analyzing_opportunities' || stage === 'opportunities_analyzed') {
      return <Search className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-bounce" />;
    } else if (stage === 'querying_rag' || stage === 'extracting_keywords' || stage === 'scoring_keywords') {
      return <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />;
    } else {
      return <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />;
    }
  };
  
  const getProgressPercentage = (stage: string) => {
    switch (stage) {
      case 'analyzing_company':
        return 10;
      case 'analyzing_opportunities':
      case 'opportunities_analyzed':
        return 30;
      case 'querying_rag':
        return 50;
      case 'extracting_keywords':
        return 70;
      case 'scoring_keywords':
        return 90;
      case 'complete':
        return 100;
      default:
        return 5;
    }
  };
  
  return (
    <div className="py-8">
      {/* Clear description */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
          What's happening?
        </h3>
        <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
          We're analyzing your company's capabilities and existing opportunities to generate personalized search keywords. 
          This includes extracting service lines, analyzing opportunity patterns, and using AI to create keywords that match 
          real government procurement language.
        </p>
      </div>

      {/* Streaming status indicator */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            {getStageIcon(streamingState.stage)}
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
            </span>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
              {streamingState.message || getStageDescription(streamingState.stage)}
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              {getStageDescription(streamingState.stage)}
            </div>
          </div>
          {streamingState.elapsed > 0 && (
            <div className="text-right">
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{streamingState.elapsed.toFixed(1)}s</div>
              <div className="text-xs text-blue-500 dark:text-blue-400">elapsed</div>
            </div>
          )}
          {streamingState.isStreaming && (
            <button
              onClick={onCancel}
              className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              <StopCircle className="h-4 w-4" />
              Cancel
            </button>
          )}
        </div>
        
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-blue-700 dark:text-blue-300 font-medium">
            <span className={streamingState.stage === 'analyzing_company' || streamingState.stage === 'analyzing_opportunities' || streamingState.stage === 'opportunities_analyzed' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
              Analyze
            </span>
            <span className={streamingState.stage === 'querying_rag' || streamingState.stage === 'extracting_keywords' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
              Generate
            </span>
            <span className={streamingState.stage === 'scoring_keywords' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
              Score
            </span>
            <span className={streamingState.stage === 'complete' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
              Done
            </span>
          </div>
          <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 transition-all duration-500 ease-out relative overflow-hidden"
              style={{ width: `${getProgressPercentage(streamingState.stage)}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Partial results if available */}
      {(streamingState.keywords?.length ?? 0) > 0 && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-2">
            ✓ {streamingState.keywords?.length ?? 0} keywords generated so far...
          </p>
        </div>
      )}
    </div>
  );
}


